import { STChecklistItem, STDocument, STMissingReason } from '@st/sdk'
import {
  Button,
  CircleXIcon,
  Dialog,
  DialogButtons,
  MiniDocumentDropZone,
  Modal,
  PaperClipIcon,
  PencilIcon,
  Select,
  Spinner
} from '@st/theme'
import { cuid } from '@st/util/cuid'
import { Progress } from '@st/util/progress'
import { clsx } from 'clsx'
import { useState } from 'react'
import { match, P } from 'ts-pattern'
import { useSDK } from '~/features/app-deps-provider'
import { documentUploadsModule, Upload, uploadDocument } from '~/features/document-uploads'
import { useProcess, useProcessState } from '~/store'
import { ConfirmDeleteDocumentDialog } from '../formation-viewer/formation-wizard/confirm-delete-document-dialog'
import {
  selDocumentChecklistItems,
  selDocumentsUnlinkedToChecklistItems,
  selMissingReasonOptionsListByConstraintId,
  stFolderModule
} from '../st-folder-viewer/st-folder-module'
import { stQuestionnaireModule } from './st-questionnaire-module'
import { ClientCard, DocumentChecklistItem } from './ui'

import { DocumentType } from '@st/ui-config'
import { uuidv7 } from '@st/util/uuidv7'

type UploadCardAction =
  | { type: 'confirmDelete'; doc: STDocument }
  | { type: 'setIncompleteReason'; checklistItem: STChecklistItem }
  | undefined

type Props = {
  documentType?: DocumentType
  step?: string
  variant?: 'card' | 'dialog'
  isLocked?: boolean
  onClose?: () => void
}
export function ClientUploadDocumentsCard({
  documentType,
  step,
  variant = 'card',
  isLocked,
  onClose
}: Props) {
  const sdk = useSDK()
  const documentUploads = useProcess(documentUploadsModule)
  const stQuestionnaire = useProcess(stQuestionnaireModule)

  const folder = useProcessState(stFolderModule, (s) => s.folderState!.folder)
  const documentChecklistItems = useProcessState(stFolderModule, selDocumentChecklistItems)
  const documents = useProcessState(stFolderModule, (s) => s.folderState!.documents)
  const otherDocuments = useProcessState(stFolderModule, selDocumentsUnlinkedToChecklistItems)
  const uploads = useProcessState(documentUploadsModule, (s) => Object.values(s.uploads))
  const missingReasonOptionsByConstraintId = useProcessState(stFolderModule, (s) =>
    selMissingReasonOptionsListByConstraintId(s.folderState!)
  )

  const [uploadCardAction, setUploadCardAction] = useState<UploadCardAction>(undefined)

  return (
    <>
      <ClientUploadDocumentsView
        step={step}
        variant={variant}
        isLocked={isLocked}
        documentChecklistItems={documentChecklistItems}
        missingReasonOptionsByConstraintId={missingReasonOptionsByConstraintId}
        documents={documents}
        uploads={uploads}
        otherDocuments={otherDocuments}
        onClose={onClose}
        onUploadFileToChecklistItem={(file: File, checklistItemId: string) => {
          documentUploads.send(
            uploadDocument({
              file,
              checklistItemId,
              uploadId: uuidv7(),
              folderId: folder.id,
              documentTypeId: documentType?.id
            })
          )
        }}
        onClickDocument={(doc: STDocument) => {
          window.open(doc.urls.sourceInline, '_blank')
        }}
        onClickDeleteDocument={(doc: STDocument) =>
          setUploadCardAction({ type: 'confirmDelete', doc })
        }
        onClickSetIncompleteReason={(checklistItem: STChecklistItem) =>
          setUploadCardAction({ type: 'setIncompleteReason', checklistItem })
        }
        onUploadFilesToOther={(files: File[]) => {
          files.forEach((file: File) => {
            documentUploads.send(
              uploadDocument({
                file,
                uploadId: uuidv7(),
                folderId: folder.id,
                documentTypeId: documentType?.id,
                questionnaireDocumentTypeId: documentType?.id
              })
            )
          })
        }}
      />
      {match(uploadCardAction)
        .with({ type: 'confirmDelete' }, ({ doc }) => (
          <Modal isOpen={true}>
            <ConfirmDeleteDocumentDialog
              doc={doc}
              onConfirm={() => {
                stQuestionnaire.send({ type: 'deleteDocument', docId: doc.id })
                setUploadCardAction(undefined)
              }}
              onCancel={() => setUploadCardAction(undefined)}
            />
          </Modal>
        ))
        .with({ type: 'setIncompleteReason' }, ({ checklistItem }) => (
          <Modal isOpen={true}>
            <SetChecklistItemIncompleteReasonDialog
              checklistItem={checklistItem}
              missingReasonOptions={
                missingReasonOptionsByConstraintId[checklistItem.constraint.id] ?? []
              }
              onEditReason={(reason) => {
                setUploadCardAction(undefined)
                sdk.send({
                  type: 'folders/setChecklistItemIncompleteReasons',
                  folderId: folder.id,
                  options: [
                    {
                      checklistItemId: checklistItem.id,
                      incompleteReason: reason
                    }
                  ]
                })
              }}
              onRemoveReason={() => {
                setUploadCardAction(undefined)
                sdk.send({
                  type: 'folders/setChecklistItemIncompleteReasons',
                  folderId: folder.id,
                  options: [{ checklistItemId: checklistItem.id, incompleteReason: undefined }]
                })
              }}
              onClose={() => setUploadCardAction(undefined)}
            />
          </Modal>
        ))
        .otherwise(() => null)}
    </>
  )
}

type ClientUploadDocumentsViewProps = {
  step?: string
  documentChecklistItems: STChecklistItem[]
  missingReasonOptionsByConstraintId: Record<string, STMissingReason[]>
  documents: STDocument[]
  uploads: Upload[]
  otherDocuments: STDocument[]
  onUploadFileToChecklistItem: (file: File, checklistItemId: string) => void
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onClickSetIncompleteReason: (checklistItem: STChecklistItem) => void
  onUploadFilesToOther: (files: File[]) => void
  variant?: 'card' | 'dialog'
  isLocked?: boolean
  onClose?: () => void
}
function ClientUploadDocumentsView({
  step,
  documentChecklistItems,
  missingReasonOptionsByConstraintId,
  documents,
  uploads,
  otherDocuments,
  onUploadFileToChecklistItem,
  onClickDocument,
  onClickDeleteDocument,
  onClickSetIncompleteReason,
  onUploadFilesToOther,
  variant = 'card',
  isLocked,
  onClose
}: ClientUploadDocumentsViewProps) {
  const content = (
    <div className="flex flex-col gap-4">
      <UploadDocumentsChecklist
        className={variant === 'card' ? '-mx-6 -mt-6' : ''}
        documentChecklistItems={documentChecklistItems}
        missingReasonOptionsByConstraintId={missingReasonOptionsByConstraintId}
        documents={documents}
        uploads={uploads}
        isLocked={isLocked}
        onClickDocument={onClickDocument}
        onClickDeleteDocument={onClickDeleteDocument}
        onClickSetIncompleteReason={onClickSetIncompleteReason}
        onUpload={onUploadFileToChecklistItem}
      />
      <UploadOtherDocuments
        title={documentChecklistItems.length > 0 ? 'Other documents' : 'Documents'}
        uploads={uploads}
        otherDocuments={otherDocuments}
        isLocked={isLocked}
        onClickDocument={onClickDocument}
        onClickDeleteDocument={onClickDeleteDocument}
        onUpload={onUploadFilesToOther}
      />
    </div>
  )

  if (variant == 'dialog') {
    return (
      <Dialog
        title="Upload your documents"
        className="max-w-4xl"
        buttons={
          <DialogButtons>
            <Button variant="primary" onClick={onClose}>
              OK
            </Button>
          </DialogButtons>
        }
      >
        {content}
      </Dialog>
    )
  }

  return (
    <ClientCard step={step} title="Upload your documents">
      {content}
    </ClientCard>
  )
}

type UploadDocumentsChecklistProps = {
  documentChecklistItems: STChecklistItem[]
  missingReasonOptionsByConstraintId: Record<string, STMissingReason[]>
  documents: STDocument[]
  uploads: Upload[]
  isLocked?: boolean
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onClickSetIncompleteReason: (checklistItem: STChecklistItem) => void
  onUpload: (file: File, checklistItemId: string) => void
  className?: string
}

function UploadDocumentsChecklist({
  documentChecklistItems,
  missingReasonOptionsByConstraintId,
  documents,
  uploads,
  isLocked,
  onClickDocument,
  onClickDeleteDocument,
  onClickSetIncompleteReason,
  onUpload,
  className
}: UploadDocumentsChecklistProps) {
  return (
    <div className={className}>
      {documentChecklistItems.map((item) => {
        const incompleteReasonOptions = missingReasonOptionsByConstraintId[item.constraint.id]

        const document = documents.find((doc) => doc.id == item.reference?.documentId)
        const incompleteReason = incompleteReasonOptions?.find(
          (option) => option.key == item.incompleteReason
        )
        const upload = uploads.find((u) => u.checklistItemId == item.id)

        return (
          <DocumentChecklistItem
            key={item.id}
            title={item.name}
            description={item.note}
            completed={item.status == 'complete'}
            buttons={match({ incompleteReason, document })
              .with({ document: P.nonNullable }, ({ document }) => {
                return (
                  <div className="flex cursor-pointer items-center gap-2 text-base text-gray-700">
                    <PaperClipIcon className="size-3" />
                    <div className="h-6" onClick={() => onClickDocument(document)}>
                      {document.originalName}
                    </div>
                    {isLocked ? null : (
                      <button type="button" onClick={() => onClickDeleteDocument(document)}>
                        <CircleXIcon className="size-3" />
                      </button>
                    )}
                  </div>
                )
              })
              .with({ incompleteReason: P.nonNullable }, ({ incompleteReason }) => {
                return (
                  <Button
                    className="rounded-full pl-2"
                    variant="default"
                    trailingIcon={<PencilIcon className="size-4" />}
                    onClick={() => onClickSetIncompleteReason(item)}
                  >
                    <div />
                    {incompleteReason.name}
                  </Button>
                )
              })
              .otherwise(() => (
                <div className="flex gap-2">
                  <Button variant="default" onClick={() => onClickSetIncompleteReason(item)}>
                    Not applicable
                  </Button>
                  <Button
                    type="upload"
                    leadingIcon={
                      upload ? (
                        <Spinner className="size-4 fill-blue-500 text-blue-100" />
                      ) : (
                        <UploadArrowIcon className="size-4" />
                      )
                    }
                    onUpload={(file) => {
                      onUpload(file, item.id)
                    }}
                  >
                    Upload
                  </Button>
                </div>
              ))}
          />
        )
      })}
    </div>
  )
}

type UploadOtherDocumentsProps = {
  title: string
  uploads: Upload[]
  otherDocuments: STDocument[]
  isLocked?: boolean
  onClickDocument: (doc: STDocument) => void
  onClickDeleteDocument: (doc: STDocument) => void
  onUpload: (files: File[]) => void
}

function UploadOtherDocuments({
  title,
  uploads,
  otherDocuments,
  isLocked,
  onClickDocument,
  onClickDeleteDocument,
  onUpload
}: UploadOtherDocumentsProps) {
  return (
    <div className="border-gray-200">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      <MiniDocumentDropZone
        caption="Upload any documents that are not listed above"
        onDrop={(dt) => {
          const files = [...dt.files]
          if (files.length > 0) {
            onUpload(files)
          }
        }}
      />

      <div>
        {uploads
          .filter((u) => u.checklistItemId == null)
          .map((u) => {
            return <OtherDocument key={u.id} documentName={u.filename} progress={u.progress} />
          })}

        {/* The bottom margin makes the items more flush with the card, but if there are no items we don't want kill the padding */}
        <div className={clsx('-mx-6 -mb-6', otherDocuments.length == 0 && 'hidden')}>
          {otherDocuments.map((doc) => (
            <OtherDocument
              key={doc.id}
              documentName={doc.originalName!}
              isLocked={isLocked}
              onClickDocument={() => onClickDocument(doc)}
              onClickDelete={() => onClickDeleteDocument(doc)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function UploadIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <g clipPath="url(#clip0_4593_105067)">
        <path
          d="M8 16C9.58225 16 11.129 15.5308 12.4446 14.6518C13.7602 13.7727 14.7855 12.5233 15.391 11.0615C15.9965 9.59966 16.155 7.99113 15.8463 6.43928C15.5376 4.88743 14.7757 3.46197 13.6569 2.34315C12.538 1.22433 11.1126 0.462404 9.56072 0.153721C8.00887 -0.154959 6.40034 0.00346672 4.93853 0.608967C3.47672 1.21447 2.22729 2.23985 1.34824 3.55544C0.469191 4.87103 -1.06964e-06 6.41775 -1.20797e-06 8C0.00223783 10.121 0.845812 12.1546 2.34562 13.6544C3.84542 15.1542 5.87895 15.9978 8 16ZM8 1.23077C9.33883 1.23077 10.6476 1.62778 11.7608 2.37159C12.874 3.11541 13.7416 4.17261 14.254 5.40953C14.7663 6.64644 14.9004 8.00751 14.6392 9.32061C14.378 10.6337 13.7333 11.8399 12.7866 12.7866C11.8399 13.7333 10.6337 14.378 9.32061 14.6392C8.00751 14.9004 6.64644 14.7663 5.40952 14.254C4.17261 13.7416 3.1154 12.874 2.37159 11.7608C1.62778 10.6476 1.23077 9.33883 1.23077 8C1.2328 6.20531 1.94664 4.48472 3.21568 3.21568C4.48471 1.94665 6.20531 1.23281 8 1.23077ZM5.10307 7.82C5.04586 7.76285 5.00047 7.69498 4.9695 7.62027C4.93853 7.54557 4.92259 7.46549 4.92259 7.38462C4.92259 7.30375 4.93853 7.22367 4.9695 7.14896C5.00047 7.07425 5.04586 7.00638 5.10307 6.94923L7.56461 4.48769C7.62177 4.43048 7.68964 4.38509 7.76434 4.35412C7.83905 4.32315 7.91913 4.30721 8 4.30721C8.08087 4.30721 8.16095 4.32315 8.23565 4.35412C8.31036 4.38509 8.37823 4.43048 8.43538 4.48769L10.8969 6.94923C11.0124 7.0647 11.0773 7.22132 11.0773 7.38462C11.0773 7.54792 11.0124 7.70453 10.8969 7.82C10.7814 7.93547 10.6248 8.00034 10.4615 8.00034C10.2982 8.00034 10.1416 7.93547 10.0262 7.82L8.61538 6.40846L8.61538 11.0769C8.61538 11.2401 8.55055 11.3967 8.43514 11.5121C8.31973 11.6275 8.16321 11.6923 8 11.6923C7.83679 11.6923 7.68026 11.6275 7.56485 11.5121C7.44945 11.3967 7.38461 11.2401 7.38461 11.0769L7.38461 6.40846L5.97384 7.82C5.91669 7.87722 5.84882 7.92261 5.77412 7.95358C5.69941 7.98455 5.61933 8.00049 5.53846 8.00049C5.45759 8.00049 5.37751 7.98455 5.3028 7.95358C5.2281 7.92261 5.16023 7.87722 5.10307 7.82Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_4593_105067">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

function UploadArrowIcon({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M11.47 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1-1.06 1.06l-3.22-3.22V16.5a.75.75 0 0 1-1.5 0V4.81L8.03 8.03a.75.75 0 0 1-1.06-1.06l4.5-4.5ZM3 15.75a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function OtherDocument({
  documentName,
  progress,
  onClickDocument,
  onClickDelete,
  isLocked
}: {
  documentName: string
  progress?: Progress
  onClickDocument?: () => void
  onClickDelete?: () => void
  isLocked?: boolean
}) {
  return (
    <div
      className={clsx(
        'flex flex-col items-end justify-end border-b border-gray-200 px-6 py-3 last:border-b-0'
      )}
    >
      <div className="flex cursor-pointer items-center gap-2 text-base text-gray-700">
        <PaperClipIcon className="size-3" />
        <div className="h-6" onClick={onClickDocument}>
          {documentName}
        </div>
        {isLocked ? null : (
          <button type="button" onClick={onClickDelete}>
            <CircleXIcon className="size-3" />
          </button>
        )}
      </div>
      {progress && (
        <div className="h-1 w-full bg-gray-300">
          <div className="h-full bg-blue-500" style={{ width: Progress.toString(progress) }} />
        </div>
      )}
    </div>
  )
}

function SetChecklistItemIncompleteReasonDialog({
  checklistItem,
  missingReasonOptions,
  onEditReason,
  onRemoveReason,
  onClose
}: {
  checklistItem: STChecklistItem
  missingReasonOptions: STMissingReason[]
  onEditReason: (reason: string) => void
  onRemoveReason: () => void
  onClose: () => void
}) {
  const [editedValue, setValue] = useState<string | undefined>(checklistItem.incompleteReason)

  return (
    <Dialog
      title="Not applicable"
      subtitle="If this is no longer applicable or you can't provide it yet, please select a reason below."
      buttons={
        <DialogButtons>
          <Button
            variant="default"
            onClick={() => {
              if (checklistItem.incompleteReason != undefined) {
                onRemoveReason()
              } else {
                onClose()
              }
            }}
          >
            {checklistItem.incompleteReason ? 'Remove reason' : 'Cancel'}
          </Button>
          <Button
            onClick={() => {
              if (editedValue != checklistItem.incompleteReason && editedValue != undefined) {
                onEditReason(editedValue)
              } else {
                onClose()
              }
            }}
          >
            Save reason
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-row p-1">
        <Select
          placeholder="Select a reason"
          className="w-full"
          options={missingReasonOptions}
          value={editedValue}
          buildValue={(option) => option.key}
          buildLabel={(option) => option.name}
          onChange={(value) => {
            setValue(value)
          }}
        />
      </div>
    </Dialog>
  )
}
